/**
 * When a participant stops sending a track (screen share stopped, camera off), drop the
 * matching consumer's track from their tile. Without this the tile kept the dead track,
 * which stays "live" and shows a frozen last frame, so it looked like they were still sharing.
 */
export interface PeerStreams {
  userId: string;
  stream: MediaStream;
  consumerIds: Set<string>;
  videoConsumerId?: string;
}

export function removeConsumerTrack<P extends PeerStreams>(
  peers: Map<string, P>, userId: string, consumerId: string, track: MediaStreamTrack,
): Map<string, P> {
  const p = peers.get(userId);
  if (!p || !p.consumerIds.has(consumerId)) return peers;
  const next = new Map(peers);
  const consumerIds = new Set(p.consumerIds);
  consumerIds.delete(consumerId);
  // A new MediaStream so React sees a new reference and the tile re-renders
  const stream = new MediaStream(p.stream.getTracks().filter(t => t !== track && t.id !== track.id));
  next.set(userId, {
    ...p,
    stream,
    consumerIds,
    videoConsumerId: p.videoConsumerId === consumerId ? undefined : p.videoConsumerId,
  });
  return next;
}

/**
 * Only the participant who owns a producer may announce it closed: the signaling server
 * stamps `from` with the sender's id, so compare against that, not the message body.
 */
export function mayCloseProducer(owner: string | undefined, from: string | undefined): boolean {
  return !!owner && !!from && owner === from;
}
